import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { requestApi, type RequestDecline } from '../../api/request_api';
import { useAuthStore } from '../../stores/root_store';

/** Recent "Hide this task" declines across all requests: who hid what, and why. */
export const ManagerDeclinesPage = observer(() => {
  const authStore = useAuthStore();
  const isManager = authStore.roles.includes('manager');
  const [declines, setDeclines] = useState<RequestDecline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const loadDeclines = async (loadCursor?: string) => {
    setLoading(true);
    try {
      const response = await requestApi.listRecentDeclines(loadCursor);
      setDeclines(prev => loadCursor ? [...prev, ...response.items] : response.items);
      setCursor(response.cursor || null);
      setHasMore(response.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load declines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isManager) loadDeclines();
  }, [isManager]);

  if (!isManager) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button component={RouterLink} to="/manager" startIcon={<ArrowBackIcon />}>
          Manager
        </Button>
        <Typography variant="h4">Recent Declines</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tasks assistants hid from their Available list, newest first.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Assistant</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Request</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {declines.map(d => (
                <TableRow key={d.key}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(d.date_created).toLocaleString()}
                  </TableCell>
                  <TableCell>{d.user_name}</TableCell>
                  <TableCell>{d.reason_label}</TableCell>
                  <TableCell>
                    {d.request_key ? (
                      <Link component={RouterLink} to={`/assistant/request/${d.request_key}`}>
                        {d.request_title || 'Untitled Request'}
                      </Link>
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {declines.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No declines yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Button variant="outlined" onClick={() => loadDeclines(cursor || undefined)}>
              Load More
            </Button>
          </Box>
        )}
      </Card>
    </Container>
  );
});
